import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { createExternalDevices } from '~/javascripts/admin/actions/devices';
import { fetchOrganizations } from '~/javascripts/admin/actions/organization';
import { fetchShops } from '~/javascripts/admin/actions/shops';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import Button from '../../atoms/Buttons';
import FormErrorMessage from '../../atoms/FormErrorMessage';
import Grid from '../../atoms/Grid';
import Headings from '../../atoms/Headings';
import Input from '../../atoms/Input';
import Label from '../../atoms/Label';
import Required from '../../atoms/Required';
import Select from '../../atoms/Select';
import ApiResults from '../../molecules/ApiResults';
import BreadCrumb from '../../molecules/BreadCrumb';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';

const PageWrapper = styled.div``;

const SectionWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    margin-left: ${spacing['8']};
  `}
`;

const GridContainer = styled(Grid.Container)`
  ${({ theme: { spacing } }) => css`
    ${Grid.Item} {
      margin: ${spacing['8']} 0;

      ${Label} {
        margin-bottom: ${spacing['0']};
        align-self: center;
      }

      ${Select as any}, ${Input}, ${FormErrorMessage} {
        margin-left: ${spacing['12']};
      }
    }
  `}
`;

type CreateDeviceKeysFormData = {
  organization: { code: string; name: string } | null;
  shop: { id: string; name: string } | null;
  num_devices: number | null;
};

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '各種設定', href: '', isCurrentPage: true },
  { value: '社外デバイス管理', href: '/settings/device-keys', isCurrentPage: false },
  { value: '社外デバイス新規登録', href: '', isCurrentPage: true },
];

const toOption = <T extends { name: string }>(item: T) => ({
  label: item.name,
  value: item,
});

const CreateDeviceKeys = () => {
  const { id: privateMoneyId } = useAppSelector(selectUserPrivateMoney);

  const [organizations, setOrganizations] = useState<{ code: string; name: string }[]>([]);
  const [shops, setShops] = useState<{ id: string; name: string }[]>([]);
  const [apiMessages, setApiMessages] = useState<{ text: string; isError?: boolean }[] | undefined>(
    undefined
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreateDeviceKeysFormData>({
    defaultValues: {
      organization: null,
      shop: null,
      num_devices: 1,
    },
  });

  const selectedOrganization = watch('organization');

  const onSubmit = (data: CreateDeviceKeysFormData) => {
    if (!privateMoneyId || !data.shop || !data.num_devices) {
      return;
    }

    setApiMessages(undefined);

    createExternalDevices({
      private_money_id: privateMoneyId,
      shop_id: data.shop.id,
      num_devices: data.num_devices,
    })
      .then(() => {
        setApiMessages([
          {
            text: '社外デバイスの登録に成功しました',
            isError: false,
          },
        ]);

        reset();
      })
      .catch((error) => {
        setApiMessages([
          {
            text: error.message || '社外デバイスの登録に失敗しました',
            isError: true,
          },
        ]);
      });
  };

  useEffect(() => {
    if (privateMoneyId) {
      fetchOrganizations(`private_money_id=${privateMoneyId}&with_issuer_org=true&per_page=10000`).then((orgs) => {
        setOrganizations(orgs.rows as { code: string; name: string }[]);
      });
    }
  }, [privateMoneyId]);

  useEffect(() => {
    if (selectedOrganization && privateMoneyId) {
      fetchShops(
        `organization_code=${selectedOrganization.code}&private_money_id=${privateMoneyId}`
      ).then((res) => {
        setShops(res as { id: string; name: string }[]);
      });
    } else {
      setShops([]);
      setValue('shop', null);
    }
  }, [selectedOrganization, privateMoneyId, setValue]);

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <Headings.h5>社外デバイス新規登録</Headings.h5>
      <SectionWrapper>
        <SelectedPrivateMoneyDisplay />
        <GridContainer>
          <Grid.Item size={1}>
            <Label>
              組織名 <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={3} flexDirection="column">
            <Controller
              control={control}
              name="organization"
              rules={{ required: '組織名を選択してください' }}
              render={({ field: { value, onChange } }) => (
                <Select
                  fullWidth
                  isMulti={false}
                  options={organizations.map(toOption)}
                  value={value ? toOption(value) : null}
                  onChange={(v) => {
                    if (!v) return;
                    onChange(v.value);
                  }}
                  placeholder="「組織」を選択"
                />
              )}
            />
            <FormErrorMessage name="organization" errors={errors} />
          </Grid.Item>
          <Grid.Item size={1} nextRow>
            <Label>
              店舗名 <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={3} flexDirection="column">
            <Controller
              control={control}
              name="shop"
              rules={{ required: '店舗名を選択してください' }}
              render={({ field: { value, onChange } }) => (
                <Select
                  fullWidth
                  isMulti={false}
                  options={shops.map(toOption)}
                  value={value ? toOption(value) : null}
                  onChange={(v) => {
                    if (!v) return;
                    onChange(v.value);
                  }}
                  placeholder="「店舗」を選択"
                />
              )}
            />
            <FormErrorMessage name="shop" errors={errors} />
          </Grid.Item>
          <Grid.Item size={1} nextRow>
            <Label>
              発行数 <Required />
            </Label>
          </Grid.Item>
          <Grid.Item size={3} flexDirection="column">
            <Controller
              control={control}
              name="num_devices"
              rules={{
                required: '発行数を入力してください',
              }}
              render={({ field: { value, onChange } }) => (
                <div style={{ width: '90%' }}>
                  <Input
                    type="number"
                    syncValue={value?.toString() || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d+$/.test(val)) {
                        onChange(val === '' ? null : parseInt(val, 10));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (['-', '+', '.', 'e', 'E'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    fullWidth
                    min="1"
                  />
                </div>
              )}
            />
            <FormErrorMessage name="num_devices" errors={errors} />
          </Grid.Item>
        </GridContainer>
      </SectionWrapper>
      <Grid.Divider />
      <ApiResults messages={apiMessages} />
      <Button onClick={handleSubmit(onSubmit)}>登録</Button>
    </PageWrapper>
  );
};

export default CreateDeviceKeys;
